<template>
  <v-container
    fluid
    fill-width
    class="ma-0 pa-0"
    center="start"
    justify="start"
  >
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="pa-0 ma-0">
        <v-container
          fluid
          fill-width
          class="ma-0 pa-2"
          center="start"
          justify="start"
        >
          <v-app-bar app class="primary">
            <v-icon color="white" x-large>mdi-ear-hearing</v-icon>
            <v-toolbar-title class="white--text pl-3"
              >Patient Audiograms
            </v-toolbar-title>

            <v-spacer></v-spacer>
          </v-app-bar>

          <!-- select Study Dataset -->
          <v-sheet
            color="white"
            class="scroll rounded-lg shadow my-2 pa-0 pb-2"
          >
            <v-container fluid fill-width class="ma-0 pa-0">
              <v-row class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-card color="#eeeeee" class="pt-1">
                    <v-card-title class="primary--text pl-3 py-0"
                      >STUDY DATASET:

                      <v-select
                        v-model="selectedStudyDataset"
                        label="select audiometric study dataset"
                        :items="collections"
                        :no-data-text="
                          isLoading
                            ? 'Loading...'
                            : 'No audiometric study datasets found'
                        "
                        item-text="label"
                        return-object
                        class="px-4"
                      >
                      </v-select>
                    </v-card-title>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>

            <v-container fluid fill-width>
              <v-row>
                <v-col cols="8" class="d-flex" style="overflow: auto;">
                  <audiogram
                    v-for="(a, ai) in audiograms"
                    :key="'audiogram-' + a.analysis.collection.id + '-k-' + ai"
                    :ref="'audiogram-' + ai"
                    :analysis="a.analysis"
                    :visible-cohorts="
                      selectedStudyDataset == null || loadingDataset
                        ? []
                        : visibleCohorts
                    "
                    :title="a.title"
                    :show-title-bar="true"
                    :show-add-button="true"
                  ></audiogram>
                </v-col>

                <v-col cols="4" class="pt-5">
                  <!-- define patterns -->
                  <svg width="1" height="1" class="pa-0 ma-0">
                    <defs>
                      <pattern
                        v-for="(p, index) in patterns"
                        :id="p['id']"
                        :key="'dep-' + index"
                        patternUnits="userSpaceOnUse"
                        width="25"
                        height="25"
                        :patternTransform="p['transform']"
                      >
                        <g v-html="p['pattern']" />
                      </pattern>
                    </defs>
                  </svg>

                  <v-card v-if="isLongitudinal" color="#eeeeee" class="pt-1">
                    <v-card-title
                      :disabled="!isLongitudinal"
                      class="primary--text pl-3 py-2"
                      >Visit(s)
                    </v-card-title>
                  </v-card>

                  <div v-if="isLongitudinal" class="pb-4">
                    <v-checkbox
                      v-for="m in ['First Visit', 'Last Visit', 'Change']"
                      :key="m"
                      v-model="selectedComparisonMeasures"
                      hide-details
                      :label="
                        m +
                          (m == 'Change'
                            ? ''
                            : '  [' + visitEvents[m].join(',') + ']')
                      "
                      :value="m"
                    ></v-checkbox>
                  </div>

                  <v-card color="#eeeeee" class="pt-1">
                    <v-card-title class="primary--text pl-3 py-2"
                      >Color Key
                    </v-card-title>
                  </v-card>

                  <cohort-table
                    :cohorts="
                      selectedStudyDataset == null || loadingDataset
                        ? []
                        : [patientCohort, ...reversedVisibleCohorts]
                    "
                    :show-title-bar="false"
                    :expanded="false"
                    show-colors
                    class="pt-4"
                    :no-cohorts-message="
                      selectedStudyDataset == null
                        ? 'No dataset selected.'
                        : loadingDataset
                        ? 'Loading...'
                        : 'No study groups selected.'
                    "
                    @cohortColorChange="updateCohortColor"
                  >
                  </cohort-table>
                </v-col>
              </v-row>

              <v-row class="pa-0 ma-0">
                <v-col cols="12" class="pa-0 ma-0">
                  <!-- run ML model / external prediction -->
                  <br clear="both" />
                  <v-btn
                    v-if="mlModelAvailable"
                    med
                    color="primary"
                    class="ml-3"
                    @click="showMLModelDialog = true"
                    ><v-icon medium color="white darken-3" class="pr-1"
                      >online_prediction</v-icon
                    >
                    Run ML Model</v-btn
                  >

                  <ml-model-dialog
                    :show="showMLModelDialog"
                    :selected-patient="
                      selectedPatients.length > 0 ? selectedPatients[0] : null
                    "
                    @dialogClosed="showMLModelDialog = false"
                  >
                  </ml-model-dialog>
                </v-col>
              </v-row>
            </v-container>
          </v-sheet>

          <v-sheet color="white" class="scroll rounded-lg shadow my-2">
            <v-container fluid fill-width class="ma-0 pa-0">
              <v-row class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-card color="#eeeeee" class="pt-1">
                    <v-card-title class="primary--text pl-3 py-2"
                      ><span class="pr-2">PATIENT:</span>
                      <span
                        v-if="selectedPatients.length == 0"
                        class="font-italic"
                      >
                        select patient below
                      </span>
                      <span v-else>
                        {{ selectedPatients[0].subject_num }}
                      </span>

                      <v-spacer />
                      <v-toolbar-items>
                        <v-icon
                          v-if="expandPatients"
                          @click="expandPatients = false"
                          >expand_less</v-icon
                        >
                        <v-icon v-else @click="expandPatients = true"
                          >expand_more</v-icon
                        >
                      </v-toolbar-items>
                    </v-card-title>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>

            <v-container
              v-show="expandPatients"
              fluid
              fill-width
              class="ma-0 pa-2"
            >
              <!-- no dataset loaded -->
              <v-row
                v-if="selectedStudyDataset == null || loadingDataset"
                class="ma-0 pa-0"
              >
                <v-col cols="12" class="ma-0 pa-0">
                  <div
                    column
                    align-center
                    justify-center
                    fill-width
                    class="py-3"
                  >
                    <loading-spinner v-if="loadingDataset"> </loading-spinner>
                    <v-subheader
                      v-else
                      class="title primary--text text--lighten-5"
                    >
                      No dataset selected.
                    </v-subheader>
                  </div>
                </v-col>
              </v-row>

              <v-row v-else class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-text-field
                    v-model="searchText"
                    label="Search patients:"
                    dense
                    hide-details
                    clearable
                    class="pt-2 pl-2"
                  ></v-text-field>

                  <v-data-table
                    v-model="selectedPatients"
                    show-select
                    :single-select="true"
                    :items="data"
                    item-key="subject_id"
                    :headers="patientHeaders"
                    :items-per-page="5"
                    :search="searchText"
                    dense
                    class="mt-5"
                  >
                  </v-data-table>
                </v-col>
              </v-row>
            </v-container>
          </v-sheet>

          <cohort-table
            title="STUDY GROUPS"
            :cohorts="reversedCohortList"
            :select-cohorts="visibleCohorts"
            :show-title-bar="true"
            :expanded="true"
            report-max-selected-overlap
            show-select
            show-colors
            :no-cohorts-message="
              selectedStudyDataset == null
                ? 'No dataset selected.'
                : loadingDataset
                ? 'Loading...'
                : 'No study groups in selected dataset.'
            "
            @selectedCohorts="setVisibleCohorts"
            @cohortColorChange="updateCohortColor"
          >
          </cohort-table>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { getBrewerColor, assignStudyGroupColors } from '@/utils/colors';
import Audiogram from '@/components/DataExplorer/Audiogram.vue';
import CohortTable from '@/components/common/CohortTable.vue';
import MlModelDialog from '@/components/PatientAudiogram/MLModelDialog.vue';
import { getVariablePureToneAudiometryMetadata } from '@/utils/helpers';
import { mapActions, mapState } from 'vuex';
import { state, actions } from '@/store/modules/datasetManager/types';
import {
  state as deState,
  actions as deActions,
} from '@/store/modules/dataExplorer/types';
import { patterns } from '@/utils/patterns';

let COLOR_OPTIONS = [];
for (let i = 0; i < 10; ++i) {
  COLOR_OPTIONS.push(getBrewerColor('Paired', 10, i));
}
let PATIENT_COLOR = COLOR_OPTIONS[5];

export default {
  components: {
    Audiogram,
    CohortTable,
    MlModelDialog,
  },
  props: {
    // none so far
  },
  data() {
    return {
      selectedStudyDataset: null,
      loadingDataset: false,
      isLongitudinal: false,
      allowMultipleAudiograms: true,
      selectedComparisonMeasures: ['First Visit'],
      visitEvents: { 'First Visit': [], 'Last Visit': [], Change: [] },
      analysis: {
        index: 9999,
        collection: {},
        input: { comparisonField: 'value' },
        outcomeVariables: [],
      },
      audiograms: [],
      patientColor: PATIENT_COLOR,
      patterns: patterns,
      searchText: '',
      selectedPatients: [],
      expandPatients: true,
      reversedCohortList: [],
      showMLModelDialog: false,
    };
  },
  computed: {
    reversedVisibleCohorts() {
      return [...this.visibleCohorts].reverse();
    },
    ...mapState('datasetManager', {
      isLoading: state.IS_LOADING,
      collections: state.COLLECTIONS,
    }),
    ...mapState('dataExplorer', {
      collection: deState.COLLECTION,
      data: deState.DATA,
      cohorts: deState.COHORTS,
      visibleCohorts: deState.VISIBLE_COHORTS,
    }),
    patientCohort() {
      let p = null;
      if (this.selectedPatients.length > 0) p = this.selectedPatients[0];
      if (p == null) {
        return {
          id: 'patient',
          color: this.patientColor,
          label: 'no patient selected',
          subject_ids: [],
        };
      } else {
        return {
          id: 'patient',
          color: this.patientColor,
          label: p.subject_num,
          subject_ids: [p.id],
        };
      }
    },
    icd10CodeVars() {
      if (this.collection != null) {
        let ICD10_code_vars = [];
        this.collection.subject_variables_list.forEach(sv => {
          if (sv.ontology.label.match(/^ICD10_Code_\d+$/)) {
            ICD10_code_vars.push(sv.ontology);
          }
        });
        return ICD10_code_vars;
      }
      return [];
    },
    patientHeaders() {
      let headers = [];
      if (this.collection != null) {
        headers.push({ text: 'SubjectID', value: 'subject_num' });
        headers.push({ text: 'Dataset', value: 'study.study_name' });
        let extra_headers = [];

        this.collection.subject_variables_list.forEach(sv => {
          if (sv.ontology.label.match(/(age|race|gender|audiometer)/i)) {
            extra_headers.push({
              text: sv.ontology.label,
              value: sv.ontology.label,
            });
          }
        });
        let ICD10_vars = this.icd10CodeVars;

        if (ICD10_vars.length > 0) {
          headers.push({ text: 'ICD10_Codes', value: 'ICD10_Codes' });
        }
        extra_headers.forEach(h => {
          headers.push(h);
        });
      }
      // default values
      else {
        headers = [
          { text: 'SubjectID', value: 'subject_num' },
          { text: 'Age at exam', value: 'Age_at_exam' },
          { text: 'Gender', value: 'Gender' },
          { text: 'Race', value: 'Race' },
        ];
      }
      return headers.map(h => {
        return { ...h, align: 'start', sortable: true };
      });
    },
    // whether one or more ML models are available for the selected dataset
    mlModelAvailable() {
      if (this.selectedStudyDataset == null) return false;
      // TODO - check against list provided by API/backend
      // currently only have a single model that applies to NHANES v8
      var nhanesFound = false;
      this.selectedStudyDataset.studies.forEach(s => {
        if (s.study.study_name.match(/NHANES Audiometry.* v8/)) {
          nhanesFound = true;
        }
      });
      return nhanesFound;
    },
  },
  watch: {
    async selectedStudyDataset(ssd) {
      this.clearSelections();
      if (ssd != null) {
        this.loadingDataset = true;
        await this.fetchCollection(ssd.id);
        await this.fetchData();
        await this.fetchCohorts(ssd.id);
        // assign missing colors
        let cohortUpdates = assignStudyGroupColors(
          this.cohorts,
          this.collection.id
        );
        if (cohortUpdates.length > 0)
          this.saveCohortDisplayPrefs(cohortUpdates);
        this.loadingDataset = false;
      }
    },
    cohorts(ch) {
      this.reversedCohortList = [...ch]
        .filter(c => !c.label.startsWith('[O]_'))
        .reverse();
    },
    collection(c) {
      this.audiograms = [];
      let o_vars = c.observation_variables_list.map(ov => ov.ontology);
      this.isLongitudinal =
        c.studies.length > 0 && c.studies[0].study.longitudinal;
      let comparisonField = null;
      if (this.isLongitudinal) {
        comparisonField = 'firstVisit';
      } else {
        comparisonField = 'value';
      }
      this.analysis = {
        index: 9999,
        collection: c,
        input: { comparisonField: comparisonField },
        outcomeVariables: o_vars,
      };
      // find distinct first/last visits
      let firsts = {};
      let lasts = {};
      c.observation_variables_list.map(ov => {
        firsts[ov.first_visit_event] = 1;
        lasts[ov.last_visit_event] = 1;
      });
      this.visitEvents['First Visit'] = Object.keys(firsts);
      this.visitEvents['Last Visit'] = Object.keys(lasts);
      this.selectedComparisonMeasures = ['First Visit'];
    },
    analysis(a) {
      this.audiograms.forEach(aud => {
        aud.analysis.collection = a.collection;
        aud.analysis.outcomeVariables = a.outcomeVariables;
      });
    },
    data(d) {
      let icd10_vars = this.icd10CodeVars;
      if (icd10_vars.length > 0) {
        d.forEach(d => {
          d['ICD10_Codes'] = icd10_vars
            .map(v => d[v.label])
            .filter(v => v != 'N/A')
            .join(',');
        });
      }
    },
    selectedPatients(sp) {
      this.$nextTick(() => this.updatePatientAudiograms(sp));
    },
    selectedComparisonMeasures(cms) {
      const m2f = {
        'First Visit': 'firstVisit',
        'Last Visit': 'lastVisit',
        Change: 'change',
      };
      let audiograms = [];
      cms.forEach(cm => {
        let f = m2f[cm];
        let comparisonField = this.isLongitudinal ? f : 'value';
        let audiogram = {
          title: this.isLongitudinal
            ? cm +
              (cm == 'Change'
                ? ''
                : '  [' + this.visitEvents[cm].join(',') + ']')
            : 'Audiogram',
          comparisonField: comparisonField,
          analysis: {
            index: 9999,
            collection: this.analysis.collection,
            input: { comparisonField: comparisonField },
            outcomeVariables: this.analysis.outcomeVariables,
          },
        };
        audiograms.push(audiogram);
      });
      this.audiograms = audiograms;
      this.$nextTick(() => this.updatePatientAudiograms(this.selectedPatients));
    },
  },
  async mounted() {
    this.selectedStudyDataset = null;
    if (!this.isLoading) {
      await this.fetchCollections(true);
    }
  },
  methods: {
    ...mapActions('datasetManager', {
      fetchCollections: actions.FETCH_COLLECTIONS,
    }),
    ...mapActions('dataExplorer', {
      clearData: deActions.CLEAR_DATA,
      fetchData: deActions.FETCH_DATA,
      fetchCollection: deActions.FETCH_COLLECTION,
      fetchCohorts: deActions.FETCH_COHORTS,
      setCohorts: deActions.SET_COHORTS,
      setVisibleCohorts: deActions.SET_VISIBLE_COHORTS,
      saveCohortDisplayPrefs: deActions.SAVE_COHORT_DISPLAY_PREFS,
    }),
    clearSelections() {
      this.selectedPatients = [];
      this.reversedCohortList = [];
      this.setVisibleCohorts([]);
    },
    makePatientAudiogram(
      p,
      conductionType,
      pcolor,
      minFreq,
      maxFreq,
      comparisonField
    ) {
      // left/right
      let al = {
        name: p.subject_num,
        ear: 'L',
        type: conductionType,
        color: pcolor,
        freqs: {},
      };
      let ar = {
        name: p.subject_num,
        ear: 'R',
        type: conductionType,
        color: pcolor,
        freqs: {},
      };

      // affected/unaffected
      let aa = {
        name: p.subject_num,
        ear: 'A',
        type: conductionType,
        color: pcolor,
        freqs: {},
      };
      let au = {
        name: p.subject_num,
        ear: 'U',
        type: conductionType,
        color: pcolor,
        freqs: {},
      };

      // contralateral/ipsilateral
      let ac = {
        name: p.subject_num,
        ear: 'C',
        type: conductionType,
        color: pcolor,
        freqs: {},
      };
      let ai = {
        name: p.subject_num,
        ear: 'I',
        type: conductionType,
        color: pcolor,
        freqs: {},
      };

      let agram = { L: al, R: ar, A: aa, U: au, I: ai, C: ac };
      let n_values = 0;

      this.collection.observation_variables_list.forEach(ov => {
        // e.g., LE_AC_12500
        let md = getVariablePureToneAudiometryMetadata(ov);
        if (md.ear != null) {
          if (
            md.conduction == conductionType &&
            md.freq_hz >= minFreq &&
            md.freq_hz <= maxFreq
          ) {
            if (!(md.freq_hz in agram[md.ear]['freqs'])) {
              agram[md.ear]['freqs'][md.freq_hz] = [];
            }
            agram[md.ear]['freqs'][md.freq_hz].push(
              p[ov.ontology.id][comparisonField]
            );
            n_values += 1;
          }
        }
      });
      return n_values > 0 ? agram : null;
    },
    updatePatientAudiograms(patients) {
      [0, 1, 2].forEach(x => {
        let akey = 'audiogram-' + x;
        if (akey in this.$refs && typeof this.$refs[akey][0] != 'undefined') {
          let audiogram = this.$refs[akey][0];
          audiogram.removeAllUserAudiograms();
          let cfield = audiogram.analysis.input.comparisonField;

          if (patients.length > 0) {
            let air_l = this.makePatientAudiogram(
              patients[0],
              'air',
              this.patientColor,
              10, // exclude 3 and 4 PTA values
              8000,
              cfield
            );
            if (air_l != null) audiogram.addAudiogram(air_l);
            let air_h = this.makePatientAudiogram(
              patients[0],
              'air',
              this.patientColor,
              8500,
              22000,
              cfield
            );
            if (air_h != null) audiogram.addAudiogram(air_h);
            let bone_l = this.makePatientAudiogram(
              patients[0],
              'bone',
              this.patientColor,
              10,
              8000,
              cfield
            );
            if (bone_l != null) audiogram.addAudiogram(bone_l);
          }
        }
      });
    },
    updateCohortColor(cc) {
      if (cc['cohort'].id == 'patient') {
        this.patientColor = cc['color'];
        this.selectedPatients = [...this.selectedPatients];
      } else {
        this.updateCohortColorInArray(this.visibleCohorts, cc);
        this.updateCohortColorInArray(this.cohorts, cc);
      }
    },
    updateCohortColorInArray(a, cc) {
      let i = 0;
      let cnum = null;
      a.forEach(c => {
        if (c.id == cc['cohort'].id) {
          cnum = i;
        }
        i += 1;
      });
      if (cnum != null) {
        a[cnum]['color'] = cc['color'];
        if ('pattern' in cc) {
          a[cnum]['pattern'] = cc['pattern'];
        }
        // force update
        a.splice(cnum, 1, a[cnum]);
      }
    },
  },
};
</script>

<style scoped></style>
